import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  setEnrichmentStatus,
  incrementEnrichmentWizardStep,
} from 'actions/enrichmentWizard';
import { useGet } from 'hooks';
import Icon from 'components/common/Icon';
import {
  getDataEnrichmentId,
  getEnrichmentStatus,
} from 'selectors/enrichmentWizard';
import { enrichmentStatus as enrichmentStatusApi } from 'services/api';
import { MessageText } from './styled';
import Spinner from 'components/common/Spinner';

const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
  gap: 24px;
  margin: auto;
`;

const EnrichmentStep = () => {
  const dispatch = useDispatch();
  const enrichmentStatus = useSelector(getEnrichmentStatus);
  const dataEnrichmentId = useSelector(getDataEnrichmentId);

  const [{ res: status }, getStatus] = useGet({
    url: enrichmentStatusApi(dataEnrichmentId),
  });

  useEffect(() => {
    if (status) {
      if (status.isFailed) {
        dispatch(setEnrichmentStatus('error'));
      } else if (status.isCompleted) {
        dispatch(setEnrichmentStatus('complete'));
      } else {
        dispatch(setEnrichmentStatus('processing'));
      }
    }
  }, [status, dispatch]);

  useEffect(() => {
    let timeoutId;
    if (enrichmentStatus === 'complete') {
      timeoutId = setTimeout(() => {
        dispatch(incrementEnrichmentWizardStep());
      }, 2000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enrichmentStatus, dispatch]);

  useEffect(() => {
    let intervalId;
    if (enrichmentStatus === 'processing') {
      intervalId = setInterval(() => {
        getStatus();
      }, 30000);
    }

    // Cleanup timeout if component unmounts or status changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enrichmentStatus, getStatus]);

  useEffect(() => {
    if (enrichmentStatus === 'processing') {
      getStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (enrichmentStatus === 'complete') {
    return (
      <ProcessingContainer>
        <Icon icon='valid' size='5x' color='green' />
        <MessageText>Your enrichment process is complete!</MessageText>
      </ProcessingContainer>
    );
  }

  if (enrichmentStatus === 'loading' || enrichmentStatus === 'processing') {
    return (
      <ProcessingContainer>
        <Spinner size='large' />
        <MessageText>
          Your enrichment process has started. You may close this wizard and
          return later to see your results.
        </MessageText>
      </ProcessingContainer>
    );
  }

  if (enrichmentStatus === 'error') {
    return (
      <ProcessingContainer>
        <Icon icon='error' size='5x' color='red' />
        <MessageText>
          An error occurred during the enrichment process. Please try again or
          contact support.
        </MessageText>
      </ProcessingContainer>
    );
  }

  return (
    <ProcessingContainer>
      <MessageText>Enrichment process is starting...</MessageText>
    </ProcessingContainer>
  );
};

export default EnrichmentStep;
